import React, { useEffect, useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { useBrevoDeals } from '../hooks/useBrevoDeals';
import ForecastingTab from '../components/ForecastingTab';
import PulseTab from '../components/PulseTab';
import SalesStatsTab from '../components/SalesStatsTab';
import ChaseTab from '../components/ChaseTab';
import InsightsTab from '../components/InsightsTab';
import LeadTrackerTab from '../components/LeadTrackerTab';
import LoginScreen from '../components/dashboard/LoginScreen';
import SalesTab from '../components/dashboard/SalesTab';
import { getToken, setToken, clearAuth } from '../lib/authFetch';
import { MONTH_NAMES, formatCurrency } from '../components/dashboard/SharedBits';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  RefreshCw,
  AlertTriangle,
  Download,
  BarChart3,
  Calculator,
  Activity,
  LayoutDashboard,
  LogOut
} from 'lucide-react';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

export default function Dashboard() {
  const { 
    loading, 
    error, 
    progress, 
    diagnosticLogs, 
    data, 
    lastUpdated,
    fetchData, 
    refreshData,
    runDiagnostic,
    clearLogs 
  } = useBrevoDeals();
  
  const [diagnosticOpen, setDiagnosticOpen] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [passwordError, setPasswordError] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [selectedBookingMonth, setSelectedBookingMonth] = useState(null);
  const [activeTab, setActiveTab] = useState('sales');
  const [calendarBalances, setCalendarBalances] = useState({});
  const [calendarInfo, setCalendarInfo] = useState(null);

  // Get the avg sale value from the most recent month with deals
  const latestAvgSale = useMemo(() => {
    if (!data?.closedByMonth) return null;
    // closedByMonth is ordered oldest to newest; find the last month with deals
    for (let i = data.closedByMonth.length - 1; i >= 0; i--) {
      if (data.closedByMonth[i].dealsCount > 0) {
        return Math.round(data.closedByMonth[i].avgPerSale);
      }
    }
    return null;
  }, [data?.closedByMonth]);

  // Fetch confirmed balances from ICS calendar (live data)
  useEffect(() => {
    if (!isAuthenticated) return;
    const fetchCalendarBalances = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/calendar/balances`);
        if (response.ok) {
          const result = await response.json();
          setCalendarBalances(result.balancesByMonth || {});
          setCalendarInfo({ weddingCount: result.weddingCount, fetchedAt: result.fetchedAt, weddingDistribution: result.weddingDistribution });
        }
      } catch (e) {
        console.error('Failed to fetch calendar balances:', e); // eslint-disable-line no-console
      }
    };
    fetchCalendarBalances();
  }, [isAuthenticated]);

  // Check if already authenticated (valid token in session)
  useEffect(() => {
    if (getToken()) {
      setIsAuthenticated(true);
    }
  }, []);

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password }),
      });
      if (res.ok) {
        const { token } = await res.json();
        setToken(token);
        sessionStorage.setItem('wve_auth', 'true');
        setIsAuthenticated(true);
        setPasswordError(false);
      } else {
        setPasswordError(true);
      }
    } catch (err) {
      setPasswordError(true);
    }
  };

  // Auto-fetch on mount (only when authenticated)
  useEffect(() => {
    if (isAuthenticated) {
      fetchData();
    }
  }, [fetchData, isAuthenticated]);

  // Get available booking months from closed deals
  const availableBookingMonths = useMemo(() => {
    if (!data?.closedByMonth) return [];
    return data.closedByMonth.filter(m => m.dealsCount > 0);
  }, [data?.closedByMonth]);

  // Set default selected month when data loads
  useEffect(() => {
    if (availableBookingMonths.length > 0 && !selectedBookingMonth) {
      // Default to the most recent month with deals
      const recentMonth = availableBookingMonths.find(m => m.isCurrentMonth) || availableBookingMonths[availableBookingMonths.length - 1];
      if (recentMonth) {
        setSelectedBookingMonth(recentMonth.key);
      }
    }
  }, [availableBookingMonths, selectedBookingMonth]);

  // Calculate delivery distribution for selected booking month
  const deliveryDistribution = useMemo(() => {
    if (!selectedBookingMonth || !data?.processedDeals) return null;

    // Get deals from selected booking month
    const [year, month] = selectedBookingMonth.split('-').map(Number);
    const monthStart = new Date(year, month - 1, 1);
    const monthEnd = new Date(year, month, 0, 23, 59, 59, 999);

    const dealsInMonth = data.processedDeals.filter(deal => {
      if (!deal.closedDate) return false;
      const closedDateObj = new Date(deal.closedDate);
      return closedDateObj >= monthStart && closedDateObj <= monthEnd;
    });

    if (dealsInMonth.length === 0) return null;

    // Calculate total balance for these deals
    const totalBalance = dealsInMonth.reduce((sum, d) => sum + d.balanceValue, 0);
    const totalValue = dealsInMonth.reduce((sum, d) => sum + d.dealValue, 0);

    // Group by delivery month (balance due month = wedding date - 1 month)
    const deliveryMonths = {};
    let dealsWithWeddingDate = 0;

    dealsInMonth.forEach(deal => {
      if (deal.weddingDate) {
        dealsWithWeddingDate++;
        const weddingDateObj = new Date(deal.weddingDate);
        const balanceDueDate = new Date(weddingDateObj);
        balanceDueDate.setMonth(balanceDueDate.getMonth() - 1);
        
        const deliveryKey = `${balanceDueDate.getFullYear()}-${String(balanceDueDate.getMonth() + 1).padStart(2, '0')}`;
        
        if (!deliveryMonths[deliveryKey]) {
          deliveryMonths[deliveryKey] = {
            key: deliveryKey,
            month: MONTH_NAMES[balanceDueDate.getMonth()],
            year: balanceDueDate.getFullYear(),
            fullLabel: `${MONTH_NAMES[balanceDueDate.getMonth()]} ${balanceDueDate.getFullYear()}`,
            shortLabel: `${MONTH_NAMES[balanceDueDate.getMonth()]} ${String(balanceDueDate.getFullYear()).slice(2)}`,
            dealsCount: 0,
            balanceValue: 0,
            deals: []
          };
        }
        
        deliveryMonths[deliveryKey].dealsCount += 1;
        deliveryMonths[deliveryKey].balanceValue += deal.balanceValue;
        deliveryMonths[deliveryKey].deals.push(deal);
      }
    });

    // Convert to sorted array and calculate percentages
    const sortedDelivery = Object.values(deliveryMonths)
      .sort((a, b) => a.key.localeCompare(b.key))
      .map(m => ({
        ...m,
        percentOfTotal: totalBalance > 0 ? (m.balanceValue / totalBalance) * 100 : 0
      }));

    return {
      bookingMonth: `${MONTH_NAMES[month - 1]} ${year}`,
      totalDeals: dealsInMonth.length,
      dealsWithWeddingDate,
      dealsWithoutWeddingDate: dealsInMonth.length - dealsWithWeddingDate,
      totalValue,
      totalBalance,
      deliveryMonths: sortedDelivery,
      maxBalance: Math.max(...sortedDelivery.map(m => m.balanceValue), 0)
    };
  }, [selectedBookingMonth, data?.processedDeals]);

  // Chart data for delivery distribution
  const chartData = useMemo(() => {
    if (!deliveryDistribution?.deliveryMonths) return null;
    
    return {
      labels: deliveryDistribution.deliveryMonths.map(m => m.shortLabel),
      datasets: [
        {
          label: 'Balance Due',
          data: deliveryDistribution.deliveryMonths.map(m => m.balanceValue),
          backgroundColor: '#D4AF37',
          borderRadius: 6,
          borderSkipped: false,
        },
      ],
    };
  }, [deliveryDistribution]);

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        backgroundColor: '#1C1917',
        titleColor: '#FFFFFF',
        bodyColor: '#D4AF37',
        padding: 12,
        cornerRadius: 8,
        callbacks: {
          label: (context) => {
            const month = deliveryDistribution?.deliveryMonths[context.dataIndex];
            return `${formatCurrency(context.parsed.y)} (${month?.percentOfTotal.toFixed(1)}%)`;
          },
          afterLabel: (context) => {
            const month = deliveryDistribution?.deliveryMonths[context.dataIndex];
            return `${month?.dealsCount || 0} bookings`;
          },
        },
      },
    },
    scales: {
      x: {
        grid: { display: false },
        ticks: {
          font: { family: "'Manrope', sans-serif", size: 11 },
          color: '#78716C',
        },
      },
      y: {
        grid: { color: '#F5F5F4', borderDash: [4, 4] },
        ticks: {
          font: { family: "'Manrope', sans-serif" },
          color: '#78716C',
          callback: (value) => formatCurrency(value),
        },
      },
    },
  };

  // Download comprehensive CSV with all data
  const downloadAllData = () => {
    if (!data?.processedDeals) return;
    
    const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    
    const headers = [
      'Deal Name',
      'Contact Name', 
      'Booking Month',
      'Booking Year',
      'Deal Value (£)',
      'Balance Due (£)',
      'Wedding Date',
      'Balance Due Month',
      'Balance Due Year'
    ];
    
    const rows = data.processedDeals.map(deal => {
      // Get booking month from close date
      let bookingMonth = '';
      let bookingYear = '';
      if (deal.closedDate) {
        const closedDate = new Date(deal.closedDate);
        bookingMonth = MONTH_NAMES[closedDate.getMonth()];
        bookingYear = closedDate.getFullYear();
      }
      
      // Get balance due month from wedding date - 1 month
      let balanceDueMonth = '';
      let balanceDueYear = '';
      let weddingDateFormatted = '';
      if (deal.weddingDate) {
        const weddingDate = new Date(deal.weddingDate);
        weddingDateFormatted = weddingDate.toLocaleDateString('en-GB');
        
        const balanceDueDate = new Date(weddingDate);
        balanceDueDate.setMonth(balanceDueDate.getMonth() - 1);
        balanceDueMonth = MONTH_NAMES[balanceDueDate.getMonth()];
        balanceDueYear = balanceDueDate.getFullYear();
      }
      
      return [
        deal.name || '',
        deal.contactName || '',
        bookingMonth,
        bookingYear,
        deal.dealValue || 0,
        deal.balanceValue || 0,
        weddingDateFormatted,
        balanceDueMonth,
        balanceDueYear
      ];
    });
    
    // Sort by booking year then month
    rows.sort((a, b) => {
      const yearA = a[3] || 0;
      const yearB = b[3] || 0;
      if (yearA !== yearB) return yearA - yearB;
      
      const monthOrder = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      return monthOrder.indexOf(a[2]) - monthOrder.indexOf(b[2]);
    });
    
    const csvContent = [
      'WVE Forecaster - Complete Booking & Balance Distribution Report',
      `Generated: ${new Date().toLocaleString()}`,
      `Total Deals: ${data.processedDeals.length}`,
      `Total Booking Value: £${data.summary?.totalBookingValue?.toLocaleString() || 0}`,
      `Total Balance Due: £${data.summary?.totalBalanceDue?.toLocaleString() || 0}`,
      '',
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `WVE_Complete_Forecast_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Download CSV for delivery distribution
  const downloadCSV = () => {
    if (!deliveryDistribution?.deliveryMonths) return;
    
    const headers = ['Delivery Month', 'Year', 'Bookings', 'Balance Due (£)', '% of Total'];
    const rows = deliveryDistribution.deliveryMonths.map(m => [
      m.month,
      m.year,
      m.dealsCount,
      m.balanceValue,
      m.percentOfTotal.toFixed(2)
    ]);
    
    const csvContent = [
      `Booking Month: ${deliveryDistribution.bookingMonth}`,
      `Total Deals: ${deliveryDistribution.totalDeals}`,
      `Total Balance: ${deliveryDistribution.totalBalance}`,
      '',
      headers.join(','),
      ...rows.map(row => row.join(','))
    ].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `WVE_${deliveryDistribution.bookingMonth.replace(' ', '_')}_Delivery_Forecast.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Password screen
  if (!isAuthenticated) {
    return (
      <LoginScreen
        password={password}
        setPassword={setPassword}
        passwordError={passwordError}
        setPasswordError={setPasswordError}
        showPassword={showPassword}
        setShowPassword={setShowPassword}
        onSubmit={handlePasswordSubmit}
      />
    );
  }

  return (
    <div className="min-h-screen bg-stone-50" data-testid="dashboard">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-md border-b border-stone-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-6 py-4 md:px-12 lg:px-16">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-2xl md:text-3xl font-semibold text-stone-900 font-serif tracking-tight" data-testid="dashboard-title">
                WVE Forecaster
              </h1>
              <p className="text-sm text-stone-500 mt-1">Balance Income Forecaster</p>
            </div>
            <div className="flex items-center gap-3">
              {lastUpdated && (
                <span className="text-xs text-stone-400 hidden sm:block">
                  Last updated: {new Date(lastUpdated).toLocaleString()}
                </span>
              )}
              <Link to="/dashboard" data-testid="open-operator-dashboard-btn">
                <Button
                  className="bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white rounded-lg px-4 py-2.5 font-medium shadow-sm"
                >
                  <LayoutDashboard className="w-4 h-4 mr-2" />
                  Operator Dashboard
                </Button>
              </Link>
              <Button
                onClick={downloadAllData}
                disabled={!data?.processedDeals}
                variant="outline"
                className="border-stone-200 hover:bg-stone-50 hidden sm:flex"
                data-testid="download-all-btn"
              >
                <Download className="w-4 h-4 mr-2" />
                Export All
              </Button>
              <Button
                onClick={refreshData}
                disabled={loading}
                className="bg-stone-900 text-white hover:bg-stone-800 rounded-lg px-6 py-2.5 font-medium transition-colors duration-200 shadow-sm"
                data-testid="refresh-data-btn"
              >
                <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                {loading ? 'Refreshing...' : 'Refresh Data'}
              </Button>
              <Button
                onClick={() => { clearAuth(); setIsAuthenticated(false); }}
                variant="outline"
                className="border-stone-200 hover:bg-stone-50"
                data-testid="logout-btn"
                title="Log out"
              >
                <LogOut className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8 md:px-12 lg:px-16">
        {/* Progress indicator */}
        {loading && (
          <div className="mb-8 animate-slide-in" data-testid="loading-indicator">
            <Card className="bg-white border-stone-100">
              <CardContent className="p-6">
                <div className="flex items-center gap-4 mb-3">
                  <div className="animate-spin rounded-full h-5 w-5 border-2 border-gold-500 border-t-transparent" />
                  <p className="text-sm text-stone-600">{progress.message}</p>
                </div>
                {progress.total > 0 && (
                  <Progress 
                    value={(progress.current / progress.total) * 100} 
                    className="h-2 bg-stone-100"
                  />
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {/* Error display */}
        {error && !error.includes('clone') && (
          <div className="mb-8 animate-slide-in" data-testid="error-display">
            <Card className="bg-red-50 border-red-200">
              <CardContent className="p-6">
                <div className="flex items-center gap-3">
                  <AlertTriangle className="w-5 h-5 text-red-500" />
                  <p className="text-sm text-red-700">{error}</p>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full max-w-4xl grid-cols-4 sm:grid-cols-7 mb-8 bg-stone-100 p-1 rounded-xl gap-1" data-testid="main-tabs">
            <TabsTrigger 
              value="sales" 
              className="flex items-center gap-1 sm:gap-2 rounded-lg data-[state=active]:bg-white data-[state=active]:shadow-sm font-medium whitespace-nowrap px-2 sm:px-3 py-1.5 text-xs sm:text-sm"
              data-testid="sales-tab"
            >
              <BarChart3 className="w-4 h-4" />
              Sales
            </TabsTrigger>
            <TabsTrigger 
              value="forecasting" 
              className="flex items-center gap-1 sm:gap-2 rounded-lg data-[state=active]:bg-white data-[state=active]:shadow-sm font-medium whitespace-nowrap px-2 sm:px-3 py-1.5 text-xs sm:text-sm"
              data-testid="forecasting-tab"
            >
              <Calculator className="w-4 h-4" />
              Forecasting
            </TabsTrigger>
            <TabsTrigger 
              value="pulse" 
              className="flex items-center gap-1 sm:gap-2 rounded-lg data-[state=active]:bg-white data-[state=active]:shadow-sm font-medium whitespace-nowrap px-2 sm:px-3 py-1.5 text-xs sm:text-sm"
              data-testid="pulse-tab"
            >
              <Activity className="w-4 h-4" />
              Pulse
            </TabsTrigger>
            <TabsTrigger 
              value="sales-stats" 
              className="flex items-center gap-1 sm:gap-2 rounded-lg data-[state=active]:bg-white data-[state=active]:shadow-sm font-medium whitespace-nowrap px-2 sm:px-3 py-1.5 text-xs sm:text-sm"
              data-testid="sales-stats-tab"
            >
              <BarChart3 className="w-4 h-4" />
              Sales Stats
            </TabsTrigger>
            <TabsTrigger 
              value="chase" 
              className="flex items-center gap-1 sm:gap-2 rounded-lg data-[state=active]:bg-white data-[state=active]:shadow-sm font-medium whitespace-nowrap px-2 sm:px-3 py-1.5 text-xs sm:text-sm"
              data-testid="chase-tab"
            >
              <Activity className="w-4 h-4" />
              Chase
            </TabsTrigger>
            <TabsTrigger 
              value="insights" 
              className="flex items-center gap-1 sm:gap-2 rounded-lg data-[state=active]:bg-white data-[state=active]:shadow-sm font-medium whitespace-nowrap px-2 sm:px-3 py-1.5 text-xs sm:text-sm"
              data-testid="insights-tab"
            >
              <BarChart3 className="w-4 h-4" />
              Insights
            </TabsTrigger>
            <TabsTrigger 
              value="lead-tracker" 
              className="flex items-center gap-1 sm:gap-2 rounded-lg data-[state=active]:bg-white data-[state=active]:shadow-sm font-medium whitespace-nowrap px-2 sm:px-3 py-1.5 text-xs sm:text-sm"
              data-testid="lead-tracker-tab"
            >
              <Activity className="w-4 h-4" />
              Leads
            </TabsTrigger>
          </TabsList>

          {/* SALES TAB */}
          <TabsContent value="sales" data-testid="sales-tab-content">
            <SalesTab
              data={data}
              selectedBookingMonth={selectedBookingMonth}
              setSelectedBookingMonth={setSelectedBookingMonth}
              availableBookingMonths={availableBookingMonths}
              deliveryDistribution={deliveryDistribution}
              chartData={chartData}
              chartOptions={chartOptions}
              downloadCSV={downloadCSV}
              diagnosticOpen={diagnosticOpen}
              setDiagnosticOpen={setDiagnosticOpen}
              runDiagnostic={runDiagnostic}
              loading={loading}
              diagnosticLogs={diagnosticLogs}
              clearLogs={clearLogs}
            />
          </TabsContent>

          {/* FORECASTING TAB */}
          <TabsContent value="forecasting" data-testid="forecasting-tab-content">
            <ForecastingTab confirmedBalances={calendarBalances} calendarInfo={calendarInfo} latestAvgSale={latestAvgSale} />
          </TabsContent>

          {/* PULSE TAB */}
          <TabsContent value="pulse" data-testid="pulse-tab-content">
            <PulseTab />
          </TabsContent>

          <TabsContent value="sales-stats" data-testid="sales-stats-tab-content">
            <SalesStatsTab />
          </TabsContent>

          <TabsContent value="chase" data-testid="chase-tab-content">
            <ChaseTab />
          </TabsContent>

          <TabsContent value="insights" data-testid="insights-tab-content">
            <InsightsTab />
          </TabsContent>

          <TabsContent value="lead-tracker" data-testid="lead-tracker-tab-content">
            <LeadTrackerTab />
          </TabsContent>
        </Tabs>
      </main>

      {/* Footer */}
      <footer className="border-t border-stone-200 mt-12 py-6 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
          <p className="text-sm text-stone-400 text-center">
            WVE Forecaster • Balance Income Forecaster • Connected to Brevo CRM
          </p>
        </div>
      </footer>
    </div>
  );
}
